import { useEffect } from 'react';
import { isEmpty } from 'lodash';
import {
    DispatchFetchInlineBoardLinkDetails,
    InlineBoardLinkAttributes,
    InlineBoardLinkStatus,
} from './inlineBoardLinkConstants';
import { Attrs } from '@tiptap/pm/model';
import { Editor } from '@tiptap/react';

type UseSyncInlineBoardLinkDetailsProps = {
    dispatchFetchInlineBoardLinkDetails: DispatchFetchInlineBoardLinkDetails;
    attrs: Attrs;
    editor: Editor;
    getPos: () => number | null;
};

const useSyncInlineBoardLinkDetails = ({
    dispatchFetchInlineBoardLinkDetails,
    attrs,
    editor,
    getPos,
}: UseSyncInlineBoardLinkDetailsProps) => {
    useEffect(() => {
        dispatchFetchInlineBoardLinkDetails(attrs.boardId, attrs.permissionId)
            .catch((err) => {
                console.error('Failed to fetch inline board link details:', err);
                return null;
            })
            .then((details) => {
                const attributeDetails: Partial<InlineBoardLinkAttributes> = {
                    ...details,
                    status: InlineBoardLinkStatus.resolved,
                };
                const updates: Partial<InlineBoardLinkAttributes> = {};

                for (const keyStr in attributeDetails) {
                    const key = keyStr as keyof InlineBoardLinkAttributes;

                    if (!!attributeDetails[key] && attributeDetails[key] !== attrs[key]) {
                        updates[key] = attributeDetails[key];
                    }
                }

                if (isEmpty(updates)) return;

                // This performs the same function as Tiptap's `updateAttributes`, but avoids adding to the undo history
                editor.commands.command(({ tr }) => {
                    const pos = getPos();

                    if (typeof pos !== 'number') return false;

                    tr
                        // Avoid adding this to the undo history
                        .setMeta('addToHistory', false)
                        .setNodeMarkup(pos, undefined, {
                            ...attrs,
                            ...updates,
                        });

                    return true;
                });
            });
    }, []);
};

export default useSyncInlineBoardLinkDetails;
